import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { changeLanguage } from "../../redux/environmentRedux"
import { clearCart } from "../../redux/cartRedux"
import { logout, updateUser } from "../../redux/apiCalls"
import { Helmet } from "react-helmet"
import {
   CalendarToday,
   LocationSearching,
   MailOutline,
   PermIdentity,
   PhoneAndroid,
} from "@mui/icons-material"
import NoAvatar from "../../images/no_avatar.jpg"
// Components
import Navbar from "../../components/Navbar/Navbar"
import Footer from "../../components/Footer/Footer"
import Button from "../../components/Button/Button"
import Orders from "../../components/Orders/Orders"
import Favorites from "../../components/Favorites/Favorites"
import AccountAddress from "../../components/AccountAddress/AccountAddress"
// Styles
import {
   Container,
   Wrapper,
   Display,
   DisplayLeft,
   DisplayRight,
   Update,
   DisplayTop,
   UserImage,
   DisplayBottom,
   DisplayInfo,
   FavoriteProducts,
   UserWrapper,
   UserTitle,
   UserName,
   UserJob,
   UserDetailsTitle,
   UsersDetailsInfo,
   UserDetailsIcon,
   UserDetails,
   Title,
   FormItem,
   Form,
   StyledInputs,
   StyledSelect,
   StyledOption,
   StyledLabel,
   StyledInput,
   Message,
} from "./Account.styles"

const Account = () => {
   const dispatch = useDispatch()
   const navigate = useNavigate()
   const { t, i18n } = useTranslation()
   const { currentUser: user, isFetching } = useSelector((state) => state.user)
   const [errorMessage, setErrorMessage] = useState("")
   const [userInputs, setUserInputs] = useState({})
   const [deliveryAddress, setDeliveryAddress] = useState(user.deliveryAddress)
   const [invoiceAddress, setInvoiceAddress] = useState(user.invoiceAddress)

   const userTypeTranslation = {
      public: t("accountPage.value.type.t1"),
      optician: t("accountPage.value.type.t2"),
      distributor: t("accountPage.value.type.t3"),
   }

   const handleChange = (e) => {
      setUserInputs((prev) => {
         return { ...prev, [e.target.name]: e.target.value }
      })
   }

   const handleLang = (e) => {
      setUserInputs((prev) => {
         return { ...prev, language: e.target.value }
      })
      dispatch(changeLanguage(e.target.value))
      i18n.changeLanguage(e.target.value)
   }

   const handleSubmit = async (e) => {
      e.preventDefault()
      const res = await updateUser(user._id, userInputs, dispatch)
      if (res === "success") {
         navigate("/")
      } else {
         setErrorMessage(JSON.stringify(res))
      }
   }

   const handleLogOut = (e) => {
      e.preventDefault()
      dispatch(clearCart())
      logout(dispatch)
   }

   useEffect(() => {
      window.scrollTo(0, 0)
   }, [])

   useEffect(() => {
      if (typeof user === "object" && user !== null) {
         const { password, ...userToUpdate } = user
         setUserInputs(userToUpdate)
      }
      setUserInputs((prev) => {
         return {
            ...prev,
            deliveryAddress: deliveryAddress,
            invoiceAddress: invoiceAddress,
         }
      })
   }, [user, deliveryAddress, invoiceAddress])

   return (
      <>
         <Helmet>
            <title>{t("accountPage.meta.meta_title")}</title>
            <meta name="robots" content="noindex, nofollow" />
         </Helmet>
         <Container>
            <Navbar bgImageColor="light" />
            <Wrapper>
               <Button
                  text={t("accountPage.button.b1")}
                  callback={handleLogOut}
               />
               <UserWrapper>
                  <Display>
                     <DisplayLeft>
                        <DisplayTop>
                           <UserImage src={user.img || NoAvatar} />
                           <UserTitle>
                              <UserName>
                                 {user.firstName} {user.lastName}
                              </UserName>
                              <UserJob>
                                 {userTypeTranslation[user.type]}
                              </UserJob>
                           </UserTitle>
                        </DisplayTop>
                        <DisplayInfo>
                           <DisplayBottom>
                              <UserDetailsTitle>
                                 {t("accountPage.label.l1")}
                              </UserDetailsTitle>
                              <UserDetails>
                                 <UserDetailsIcon>
                                    <PermIdentity />
                                 </UserDetailsIcon>
                                 <UsersDetailsInfo>
                                    {user.username}
                                 </UsersDetailsInfo>
                              </UserDetails>
                              <UserDetails>
                                 <UserDetailsIcon>
                                    <CalendarToday />
                                 </UserDetailsIcon>
                                 <UsersDetailsInfo>
                                    {new Date(
                                       user.createdAt
                                    ).toLocaleDateString()}
                                 </UsersDetailsInfo>
                              </UserDetails>
                           </DisplayBottom>
                           <DisplayBottom>
                              <UserDetailsTitle>
                                 {t("accountPage.label.l2")}
                              </UserDetailsTitle>
                              <UserDetails>
                                 <UserDetailsIcon>
                                    <PhoneAndroid />
                                 </UserDetailsIcon>
                                 <UsersDetailsInfo>
                                    {user.phone}
                                 </UsersDetailsInfo>
                              </UserDetails>
                              <UserDetails>
                                 <UserDetailsIcon>
                                    <MailOutline />
                                 </UserDetailsIcon>
                                 <UsersDetailsInfo>
                                    {user.email}
                                 </UsersDetailsInfo>
                              </UserDetails>
                              <UserDetails>
                                 <UserDetailsIcon>
                                    <LocationSearching />
                                 </UserDetailsIcon>
                                 <UsersDetailsInfo>
                                    {user.deliveryAddress?.City},{" "}
                                    {user.deliveryAddress?.Country}
                                 </UsersDetailsInfo>
                              </UserDetails>
                           </DisplayBottom>
                        </DisplayInfo>
                     </DisplayLeft>
                     <DisplayRight>
                        <Title>{t("accountPage.title.t1")}</Title>
                        <Orders userId={user._id} />
                     </DisplayRight>
                  </Display>
                  <FavoriteProducts>
                     <Title>{t("accountPage.title.t2")}</Title>
                     {user.favoriteProducts ? (
                        <Favorites products={user.favoriteProducts} />
                     ) : null}
                  </FavoriteProducts>
                  <Update>
                     <Title>{t("accountPage.title.t3")}</Title>
                     <Form>
                        <StyledInputs>
                           <FormItem>
                              <StyledLabel>
                                 {t("accountPage.label.l3")}
                              </StyledLabel>
                              <StyledInput
                                 type="text"
                                 name="firstName"
                                 placeholder={user.firstName}
                                 onChange={handleChange}
                              />
                           </FormItem>
                           <FormItem>
                              <StyledLabel>
                                 {t("accountPage.label.l4")}
                              </StyledLabel>
                              <StyledInput
                                 type="text"
                                 name="lastName"
                                 placeholder={user.lastName}
                                 onChange={handleChange}
                              />
                           </FormItem>
                           <FormItem>
                              <StyledLabel>
                                 {t("accountPage.label.l5")}
                              </StyledLabel>
                              <StyledSelect
                                 name="language"
                                 id="language"
                                 defaultValue={user.language}
                                 onChange={handleLang}
                              >
                                 <StyledOption value="en">English</StyledOption>
                                 <StyledOption value="fr">
                                    Français
                                 </StyledOption>
                              </StyledSelect>
                           </FormItem>
                           <FormItem>
                              <StyledLabel>
                                 {t("accountPage.label.l6")}
                              </StyledLabel>
                              <StyledInput
                                 type="text"
                                 name="phone"
                                 placeholder={user.phone}
                                 onChange={handleChange}
                              />
                           </FormItem>
                           <FormItem>
                              <StyledLabel>
                                 {t("accountPage.label.l7")}
                              </StyledLabel>
                              <StyledInput
                                 type="text"
                                 name="companyName"
                                 placeholder={user.companyName}
                                 onChange={handleChange}
                              />
                           </FormItem>
                           <AccountAddress
                              role="delivery"
                              address={deliveryAddress}
                              setAddress={setDeliveryAddress}
                           />
                           <AccountAddress
                              role="invoice"
                              address={invoiceAddress}
                              setAddress={setInvoiceAddress}
                           />
                        </StyledInputs>
                        <Button
                           callback={(e) => handleSubmit(e)}
                           text={t("accountPage.button.b2")}
                           disabled={isFetching}
                           submit
                        />
                     </Form>
                     <Message>{errorMessage}</Message>
                  </Update>
               </UserWrapper>
            </Wrapper>
            <Footer />
         </Container>
      </>
   )
}

export default Account
